import { useGetSFTPFilePreviewMutation } from '@/services/endpoints/files/filesApi';
import { FormikProps } from 'formik';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import { Step } from '../../../../../../components/steppers';
import { DataSourceSFTPConfig, DataSourceType, TDataSource } from '../../../../../../models/dataSource';
import { GroupedTraits } from '../../../../../../models/traits';
import { RootState, useAppDispatch } from '../../../../../../reducers';
import { getS3FilePreviewKey } from '../../../../../../services/endpoints/files';
import { updateFileData } from '../../../../../fileUploading/fileUploadingSlice';
import { SFTPSteps } from '../../../../constants';
import { TColumnItem } from '../../../../types';
import FileDataSourceColumnMap from '../../file/FileDataSourceColumnMap';
import FileDataType from '../../file/FileDataType';
import { FileStepContentWrapper } from '../../file/FileStepContentWrapper';
import { SFTPConfigurationCompleted } from './SFTPConfigurationCompleted';
import SFTPDataSourceCSVHeader from './SFTPDataSourceCSVHeader';
import { SFTPFormModel, SFTPNameAndBucketStep } from './SFTPNameAndBucketStep';

type SFTPDataSourceContentProps = {
  dataSourceType: DataSourceType;
  groupedTraits?: GroupedTraits;
  step?: Step;
  goNext?: () => void;
  formRef: React.RefObject<FormikProps<SFTPFormModel>>;
  columnMapFormRef: React.RefObject<FormikProps<TColumnItem[]>>;
  loading: boolean;
};

const CSV_ROWS_NUMBER = 5;

export const SFTPDataSourceContent: FC<SFTPDataSourceContentProps> = ({ groupedTraits, step, formRef, columnMapFormRef }) => {
  const dispatch = useAppDispatch();
  const dataSource = useSelector((state: RootState) => state.dataSources.edit?.dataSource) as Partial<TDataSource<DataSourceSFTPConfig>> | undefined;

  const [getSFTPFilePreview] = useGetSFTPFilePreviewMutation({
    fixedCacheKey: getS3FilePreviewKey,
  });

  const handleSubmit = async ({ configuration: { authMethod, ...rest } }: SFTPFormModel) => {
    const response = await getSFTPFilePreview({ ...rest, limit: CSV_ROWS_NUMBER });

    if ('data' in response) {
      const matrix = response.data.map((row) => row.split(','));

      dispatch(updateFileData({ preview: matrix?.slice(0, CSV_ROWS_NUMBER), header: matrix[0], isDefaultHeader: true }));
    }
  };

  switch (step?.id) {
    case SFTPSteps.NAME_AND_HOST:
      return <SFTPNameAndBucketStep formRef={formRef} onSubmit={handleSubmit} dataSource={dataSource} />;
    case SFTPSteps.HEADER:
      return (
        <FileStepContentWrapper>
          <SFTPDataSourceCSVHeader />
        </FileStepContentWrapper>
      );
    case SFTPSteps.DATA_TYPE:
      return (
        <FileStepContentWrapper>
          <FileDataType />
        </FileStepContentWrapper>
      );
    case SFTPSteps.MAP_COLUMNS:
      return (
        <FileStepContentWrapper>
          <FileDataSourceColumnMap columnMapFormRef={columnMapFormRef} groupedTraits={groupedTraits} />
        </FileStepContentWrapper>
      );
    case SFTPSteps.CONFIRMATION:
      return (
        <FileStepContentWrapper>
          <SFTPConfigurationCompleted />
        </FileStepContentWrapper>
      );
    default:
      return null;
  }
};
